import React, { useCallback, useMemo } from "react";
import moment from "moment/moment";
import { motion } from "framer-motion";
import { useSelector } from "react-redux";
import { alpha, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Paper from "@mui/material/Paper";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import SearchIcon from "@mui/icons-material/Search";
import { parseFloatToFixed } from "../../../extras";
import { formatFinance } from "../../../utils/formatters";
import OrderActions from "./OrderActions";
import {
  getOrderBackgroundColor,
  getOrderBorderColor,
  getOrderIndexBadgeColor,
  getTotalChipStyling,
} from "../../../utils/style/orderStylingUtils";
import { ANIMATION_VARIANTS, DESIGN_TOKENS } from "../../../types/order";
import {
  BlacklistWarningChip,
  OrderTimeChip,
  OrderTypeChip,
  TotalChip,
} from "./row/OrderTimeChip";
import OrderPriorityIndicator from "./row/OrderPriorityIndicator";
import OrderMerchantAvatar from "./row/OrderMerchantAvatar";
import OrderStatusBadge from "./row/OrderStatusBadge";

export default function OrderRow({
  item,
  dashboard,
  minified,
  onlyModalAction = false,
  handleOrderModal,
  changeMapCenter,
  acceptOrder,
  handleSelectDriver,
  readyToPickupOrder,
  finishOrder,
  cancelDriverRequest,
  packedOrder,
  extraDelivery,
}) {
  const theme = useTheme();
  const ordersToDispatch = useSelector(
    (state) => state.orders.ordersToDispatch || []
  );
  const enablePackedControl = useSelector(
    (state) => state.deliveryCenter.deliveryCenter?.config.enable_packed_control
  );

  const isBlacklisted = item.is_customer_on_blacklist;
  const dateString = moment(item.delivery_expected_time).format("HH:mm");
  const formattedTotal = formatFinance(parseFloatToFixed(item.total));

  const badgeColor = useMemo(
    () => getOrderIndexBadgeColor(item, ordersToDispatch, theme),
    [item, ordersToDispatch, theme]
  );

  const backgroundColor = useMemo(
    () => getOrderBackgroundColor(item, theme),
    [item, theme]
  );

  const borderColor = useMemo(
    () => getOrderBorderColor(isBlacklisted, theme),
    [isBlacklisted, theme]
  );

  const totalChipStyling = useMemo(
    () => getTotalChipStyling(item.ranking_by_total, theme),
    [item.ranking_by_total, theme]
  );

  const handleOrderModalClick = useCallback(
    (e) => {
      e.stopPropagation();
      handleOrderModal(item, e);
    },
    [handleOrderModal, item]
  );

  const handleMapCenterClick = useCallback(
    (e) => {
      e.stopPropagation();
      changeMapCenter(item.LAT, item.LONG);
    },
    [changeMapCenter, item.LAT, item.LONG]
  );

  const handleAcceptOrder = useCallback(
    (e) => {
      e.stopPropagation();
      acceptOrder(item, e);
    },
    [acceptOrder, item]
  );

  const handleReadyToPickup = useCallback(
    (e) => {
      e.stopPropagation();
      readyToPickupOrder(item, e);
    },
    [readyToPickupOrder, item]
  );

  const handleFinishOrder = useCallback(
    (e) => {
      e.stopPropagation();
      finishOrder(item, e);
    },
    [finishOrder, item]
  );

  const handleSelectDriverClick = useCallback(
    (e) => {
      e.stopPropagation();
      handleSelectDriver(item, e);
    },
    [handleSelectDriver, item]
  );

  const handleCancelDriverRequest = useCallback(
    (e) => {
      e.stopPropagation();
      cancelDriverRequest(item, e);
    },
    [cancelDriverRequest, item]
  );

  const handlePackedOrder = useCallback(
    (e) => {
      e.stopPropagation();
      packedOrder(item, e);
    },
    [packedOrder, item]
  );

  const handleExtraDelivery = useCallback(
    (e) => {
      e.stopPropagation();
      extraDelivery(item, e);
    },
    [extraDelivery, item]
  );

  const isDeliveryOrder = item.order_type === "DELIVERY";
  const hasNewOrderAnimation = item.status_number === 0;

  const paperStyles = {
    mb: DESIGN_TOKENS.spacing.sm,
    backgroundColor: backgroundColor || theme.palette.grey[50],
    overflow: "hidden",
    position: "relative",
    "&:hover": {
      cursor: "pointer",
      borderColor: isBlacklisted
        ? alpha(theme.palette.error.main, 0.6)
        : theme.palette.grey[400],
    },
    ...(hasNewOrderAnimation && {
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `linear-gradient(90deg, ${alpha(
          theme.palette.info.light,
          0.3
        )} 0%, ${alpha(theme.palette.info.main, 0.2)} 50%, ${alpha(
          theme.palette.info.light,
          0.3
        )} 100%)`,
        backgroundSize: "200% 200%",
        animation: "shimmer 2s infinite linear",
        zIndex: 0,
      },
      "@keyframes shimmer": {
        "0%": { backgroundPosition: "0% 0%" },
        "100%": { backgroundPosition: "200% 0%" },
      },
    }),
  };

  const renderContent = (isMobile = false) => {
    const spacing = isMobile
      ? DESIGN_TOKENS.spacing.sm
      : DESIGN_TOKENS.spacing.md;

    const content = (
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={isMobile ? DESIGN_TOKENS.spacing.sm : DESIGN_TOKENS.spacing.lg}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={spacing}
          sx={{ flex: 1 }}
        >
          <OrderPriorityIndicator
            orderIndex={item.order_index}
            isBlacklisted={isBlacklisted}
            rankingByTotal={item.ranking_by_total}
            badgeColor={badgeColor}
            theme={theme}
            isMobile={isMobile}
          />

          <OrderMerchantAvatar
            channelMerchant={item.channel_merchant}
            foodTypeIcon={item.food_type_icon}
            theme={theme}
            isMobile={isMobile}
          />

          {isMobile ? (
            <>
              {isBlacklisted ? (
                <BlacklistWarningChip theme={theme} isMobile />
              ) : (
                <OrderTypeChip
                  orderType={item.order_type}
                  channelCode={item.channel_code}
                  theme={theme}
                  isMobile
                />
              )}
              <OrderTimeChip
                order={item}
                dateString={dateString}
                theme={theme}
                isMobile
              />
            </>
          ) : (
            <Stack
              direction="row"
              alignItems="center"
              spacing={spacing}
              sx={{ flex: 1 }}
            >
              {isBlacklisted && <BlacklistWarningChip theme={theme} />}

              <OrderTypeChip
                orderType={item.order_type}
                channelCode={item.channel_code}
                theme={theme}
              />

              {!minified && (
                <>
                  <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />
                  <OrderTimeChip
                    order={item}
                    dateString={dateString}
                    theme={theme}
                  />
                </>
              )}
            </Stack>
          )}
        </Stack>

        <Stack direction="row" alignItems="center" spacing={spacing}>
          <TotalChip
            total={formattedTotal}
            totalChipStyling={totalChipStyling}
            theme={theme}
            isMobile={isMobile}
          />

          <OrderStatusBadge
            statusNumber={item.status_number}
            status={item.status}
            hasExtraDelivery={item.has_extra_delivery}
            theme={theme}
            isMobile={isMobile}
          />

          {dashboard && !minified && isDeliveryOrder && !isMobile && (
            <Tooltip title="Ver no mapa">
              <IconButton
                component={motion.button}
                variants={ANIMATION_VARIANTS.icon}
                whileHover="hover"
                whileTap="tap"
                onClick={handleMapCenterClick}
                sx={{
                  p: 0.75,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.2),
                  },
                }}
                aria-label="Ver pedido no mapa"
              >
                <SearchIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          <Box
            sx={{
              bgcolor: alpha(theme.palette.grey[500], 0.1),
              borderRadius: DESIGN_TOKENS.borderRadius.sm,
              p: DESIGN_TOKENS.spacing.xs,
            }}
          >
            <OrderActions
              item={item}
              dashboard={dashboard}
              acceptOrder={handleAcceptOrder}
              enableAcceptOrder={!minified}
              readyToPickupOrder={handleReadyToPickup}
              enableReadyToPickupOrder={!onlyModalAction}
              finishOrder={handleFinishOrder}
              enableFinishOrder={!onlyModalAction}
              _handleModal={handleSelectDriverClick}
              enableHandleModal={!minified}
              cancelDriverRequest={handleCancelDriverRequest}
              enableCancelDriverRequest={!minified}
              packed={handlePackedOrder}
              enablePacked={enablePackedControl}
              extraDelivery={handleExtraDelivery}
            />
          </Box>
        </Stack>
      </Stack>
    );

    return (
      <Box
        sx={{
          px: DESIGN_TOKENS.spacing.lg,
          py: 0.7,
          position: "relative",
          zIndex: 1,
          display: isMobile
            ? { xs: "block", sm: "none" }
            : { xs: "none", sm: "block" },
        }}
      >
        {content}
      </Box>
    );
  };

  return (
    <motion.div
      variants={ANIMATION_VARIANTS.item}
      initial="hidden"
      animate="show"
      exit="exit"
      layout
    >
      <Paper
        elevation={0}
        component={motion.div}
        whileHover={{
          y: -2,
          boxShadow: theme.shadows[2],
          backgroundColor: alpha(theme.palette.background.paper, 0.9),
        }}
        onClick={handleOrderModalClick}
        sx={paperStyles}
      >
        {renderContent(false)}
        {renderContent(true)}
      </Paper>
    </motion.div>
  );
}
